"use client";

import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { cn } from "../../lib/utils";
import { DottedSurface } from "./dotted-surface";

function HeroGeometric({
  badge = "Freelance web",
  titleBefore = "Transformer vos idées en",
  titleEmphasis = "Sites web performants",
  titleAfter = "qui génèrent des résultats et inspirent confiance.",
  quote = "Donnez forme à votre vision avec mon approche",
}) {
  const fadeUp = {
    hidden: { opacity: 0, y: 28 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.85,
        delay: 0.15 + i * 0.1,
        ease: [0.22, 1, 0.36, 1],
      },
    }),
  };

  return (
    <section
      id="hero"
      aria-labelledby="hero-title"
      className="relative flex min-h-[calc(100svh-64px)] w-full items-center justify-center overflow-hidden bg-surface sm:min-h-[100svh]"
    >
      <DottedSurface className="opacity-60" />
      {/* Grid + glow — single coherent atmosphere */}
      <div
        className="pointer-events-none absolute inset-0 bg-grid-fine bg-grid opacity-[0.65]"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 bg-hero-glow"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute -left-1/4 top-1/3 h-[min(90vw,520px)] w-[min(90vw,520px)] rounded-full bg-brand/10 blur-[100px]"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute bottom-0 right-0 h-64 w-64 rounded-full bg-violet-500/5 blur-[80px]"
        aria-hidden
      />

      <div className="relative z-10 mx-auto w-full max-w-5xl px-4 pb-16 pt-20 sm:px-5 sm:pt-24 md:px-8 md:pb-28">
        <div className="mx-auto max-w-4xl text-center">
          <motion.div
            custom={0}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="mb-10 inline-flex items-center gap-2 rounded-full border border-white/[0.08] bg-white/[0.03] px-4 py-2 backdrop-blur-sm"
          >
            <Sparkles className="h-3.5 w-3.5 text-brand" aria-hidden />
            <span className="text-xs font-medium tracking-wide text-zinc-400">{badge}</span>
          </motion.div>

          <motion.h1
            id="hero-title"
            custom={1}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="font-display text-balance text-4xl font-bold leading-[1.06] tracking-tight text-white sm:text-5xl md:text-6xl lg:text-7xl"
          >
            <span className="block text-zinc-200">{titleBefore}</span>
            <span
              className={cn(
                "mt-3 block bg-gradient-to-r from-brand via-brand-bright to-amber-200 bg-clip-text text-transparent md:mt-4"
              )}
            >
              {titleEmphasis}
            </span>
          </motion.h1>

          <motion.p
            custom={2}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="mx-auto mt-5 max-w-3xl text-balance text-base font-medium leading-relaxed text-zinc-400 sm:text-lg md:mt-6 md:text-xl"
          >
            {titleAfter} Creation de site web, SEO et experiences fluides pour convertir.
          </motion.p>

          <motion.p
            custom={3}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="mx-auto mt-8 max-w-2xl text-xs font-medium uppercase tracking-[0.22em] text-zinc-500 sm:mt-10 sm:text-sm sm:tracking-[0.28em]"
          >
            {quote}
          </motion.p>

          <motion.div
            custom={4}
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            className="mt-10 flex flex-col items-stretch justify-center gap-3 sm:mt-12 sm:flex-row sm:items-center sm:gap-4"
          >
            <a
              href="#contact"
              className="inline-flex min-h-[52px] items-center justify-center rounded-2xl bg-brand px-10 text-sm font-semibold text-surface shadow-glow transition hover:bg-brand-bright"
            >
              Parlons-en
            </a>
            <a
              href="#services"
              className="inline-flex min-h-[52px] items-center justify-center rounded-2xl border border-white/10 bg-white/[0.03] px-8 text-sm font-semibold text-zinc-200 backdrop-blur transition hover:border-brand/40 hover:bg-white/[0.06]"
            >
              Découvrir les services
            </a>
          </motion.div>
        </div>
      </div>

      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-surface to-transparent" />
    </section>
  );
}

export { HeroGeometric };
